
import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from '@/components/ui/form';
import { Loader2, /* Chrome, */ Mail, Eye, EyeOff, User, CheckCircle, Wand2 } from 'lucide-react';
import { useAuthStore } from '@/lib/stores/auth';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { GoogleIcon } from '@/components/ui/google-icon';

const passwordPolicyMessage =
    'Password must be at least 8 characters and include uppercase, lowercase, a number, and a special character';
const passwordPolicy = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;

const signUpSchema = z.object({
    name: z.string().min(2, 'Name must be at least 2 characters'),
    email: z.string().email('Please enter a valid email address'),
    password: z
        .string()
        .min(8, passwordPolicyMessage)
        .regex(passwordPolicy, passwordPolicyMessage),
    confirmPassword: z.string(),
}).refine((data) => data.password === data.confirmPassword, {
    message: "Passwords don't match",
    path: ['confirmPassword'],
});

const signInSchema = z.object({
    email: z.string().email('Please enter a valid email address'),
    password: z.string().min(1, 'Password is required'),
});

type SignUpFormValues = z.infer<typeof signUpSchema>;
type SignInFormValues = z.infer<typeof signInSchema>;

interface AuthFormProps {
    mode: 'signin' | 'signup';
    showHeader?: boolean;
    showFooter?: boolean;
}

export function AuthForm({ mode, showHeader = true, showFooter = true }: AuthFormProps) {
    // const [isGoogleLoading, setIsGoogleLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);
    const [authError, setAuthError] = useState<string | null>(null);
    const navigate = useNavigate();

    const {
        loginWithEmail,
        signUpWithEmail,
        loginWithGoogle,
        error,
        clearError,
        isLoading,
        setLoading
    } = useAuthStore();

    // Guard against stuck loading without hard page reload loops.
    useEffect(() => {
        if (mode === 'signin' && isLoading) {
            const loadingTimeout = setTimeout(() => {
                console.warn('[AuthForm] Loading timeout - resetting sign-in loading state');
                setAuthError('Sign-in timed out. Please try again.');
                // Clear any pending auth flags and legacy tokens
                if (typeof window !== 'undefined') {
                    sessionStorage.removeItem('pendingGoogleAuth');
                    // Clear legacy localStorage token if present (tokens now stored in httpOnly cookies)
                    if (localStorage.getItem('auth-token')) {
                        localStorage.removeItem('auth-token');
                    }
                }
                // Never force reload here; reload can cause infinite loops if loading remains true.
                setLoading(false);
            }, 8000); // Auto-reset after 8 seconds

            return () => clearTimeout(loadingTimeout);
        }
    }, [isLoading, mode, setLoading]);


    const form = useForm<SignUpFormValues>({
        resolver: zodResolver(signUpSchema),
        defaultValues: {
            name: '',
            email: '',
            password: '',
            confirmPassword: '',
        },
    });

    const signinForm = useForm<SignInFormValues>({
        resolver: zodResolver(signInSchema),
        defaultValues: {
            email: '',
            password: '',
        },
    });

    const onSubmit = async (values: SignUpFormValues) => {
        if (mode !== 'signup') return;

        try {
            clearError();
            const result = await signUpWithEmail(values.email, values.password, values.name);
            
            if (!result.success) {
                toast.error(result.error || 'Sign up failed. Please try again.');
                return;
            }

            if (result.requiresVerification) {
                setIsSuccess(true);
                return;
            }

            if (result.user) {
                setIsSuccess(true);
                setTimeout(() => navigate('/dashboard'), 1500);
            }
        } catch (err) {
            toast.error(err instanceof Error ? err.message : 'Sign up failed. Please try again.');
        }
    };

    const onSignInSubmit = async (values: SignInFormValues) => {
        if (mode !== 'signin') return;

        try {
            clearError();
            await loginWithEmail(values.email, values.password);
            // Check for pending redirect after successful signin
            const pendingRedirect = sessionStorage.getItem('postLoginRedirect');
            if (pendingRedirect) {
                sessionStorage.removeItem('postLoginRedirect');
                navigate(pendingRedirect, { replace: true });
            } else {
                navigate('/dashboard');
            }
        } catch (err) {
            toast.error(err instanceof Error ? err.message : 'Sign in failed. Please check your credentials.');
        }
    };

    const handleGoogleSignIn = async () => {
        // console.log('[AuthForm] 🎯 BUTTON CLICKED - Starting Google sign-in...');

        try {
            setAuthError(null);
            clearError();

            // setIsGoogleLoading(true); // Uncomment if using loading state

            // Always use popup flow (works reliably in both dev and production)
            await loginWithGoogle();
        } catch (error: unknown) {
            console.error('[AuthForm] ❌ Google sign-in error:', error);

            let errorMessage = 'Failed to start Google sign-in.';
            if (error instanceof Error && error.message) {
                errorMessage = error.message;
            }

            setAuthError(errorMessage);
            // setIsGoogleLoading(false);
        }
    };

    if (mode === 'signup' && isSuccess) {
        return (
            <Card>
                <CardContent className="pt-6">
                    <div className="flex flex-col items-center justify-center space-y-4 py-6 text-center">
                        <CheckCircle className="h-12 w-12 text-success" />
                        <div className="space-y-2">
                            <h3 className="text-lg font-semibold">Check Your Email</h3>
                            <p className="text-sm text-muted-foreground">
                                We&apos;ve sent a verification link to your email address. 
                                Please click the link to activate your account.
                            </p>
                        </div>
                    </div>
                </CardContent>
            </Card>
        );
    }

    return (
        <div className="grid gap-6">
            <Card>
                {showHeader && (
                    <CardHeader className="space-y-1">
                        <CardTitle className="text-xl text-center font-semibold">
                            {mode === 'signup' ? 'Create an account' : 'Welcome back'}
                        </CardTitle>
                        <CardDescription className="text-center">
                            {mode === 'signup'
                                ? 'Sign up to access Dominion Mandate'
                                : 'Sign in to your account'
                            }
                        </CardDescription>
                    </CardHeader>
                )}
                <CardContent className="grid gap-4">
                    {(error || authError) && (
                        <Alert variant="destructive">
                            <AlertDescription>{error || authError}</AlertDescription>
                        </Alert>
                    )}

                    {/* Google Sign In */}
                    <Button
                        variant="outline"
                        onClick={handleGoogleSignIn}
                        disabled={isLoading}
                        className="w-full justify-center gap-3 border-zinc-300 bg-white px-4 py-3 text-center text-zinc-900 shadow-sm hover:bg-zinc-50 dark:bg-zinc-950 dark:text-zinc-50 dark:hover:bg-zinc-900"
                    >
                        <GoogleIcon className="size-5 shrink-0" />
                        <span>Continue with Google</span>
                    </Button>

                    {mode === 'signin' && (
                        <>
                            {/* Magic Link */}
                            <Link to="/auth/magic-link-request" className="block">
                                <Button
                                    variant="outline"
                                    size="lg"
                                    className="w-full justify-center gap-3 border-dashed bg-muted/25 px-4 text-center text-foreground/90 hover:bg-muted/45"
                                    disabled={isLoading}
                                >
                                    <Wand2 className="h-5 w-5" />
                                    <span>Sign in with magic link</span>
                                </Button>
                            </Link>

                            <div className="relative">
                                <div className="absolute inset-0 flex items-center">
                                    <span className="w-full border-t" />
                                </div>
                                <div className="relative flex justify-center text-xs uppercase">
                                    <span className="bg-background px-2 text-muted-foreground">
                                        Or continue with email
                                    </span>
                                </div>
                            </div>

                            {/* Email/Password Sign In */}
                            <Form {...signinForm}>
                                <form onSubmit={signinForm.handleSubmit(onSignInSubmit)} className="space-y-4">
                                    <FormField
                                        control={signinForm.control}
                                        name="email"
                                        render={({ field }) => (
                                            <FormItem>
                                                <FormLabel>Email</FormLabel>
                                                <FormControl>
                                                    <div className="relative">
                                                        <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                                                        <Input
                                                            placeholder="you@example.com"
                                                            type="email"
                                                            className="pl-9"
                                                            disabled={isLoading}
                                                            {...field}
                                                        />
                                                    </div>
                                                </FormControl>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />

                                    <FormField
                                        control={signinForm.control}
                                        name="password"
                                        render={({ field }) => (
                                            <FormItem>
                                                <FormLabel>Password</FormLabel>
                                                <FormControl>
                                                    <div className="relative">
                                                        <Input
                                                            placeholder="••••••••"
                                                            type={showPassword ? 'text' : 'password'}
                                                            autoComplete="current-password"
                                                            className="pr-10"
                                                            disabled={isLoading}
                                                            {...field}
                                                        />
                                                        <Button
                                                            type="button"
                                                            variant="ghost"
                                                            size="sm"
                                                            className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
                                                            onClick={() => setShowPassword(!showPassword)}
                                                            aria-label={showPassword ? 'Hide password' : 'Show password'}
                                                        >
                                                            {showPassword ? (
                                                                <EyeOff className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
                                                            ) : (
                                                                <Eye className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
                                                            )}
                                                        </Button>
                                                    </div>
                                                </FormControl>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />

                                    <Button type="submit" className="w-full" disabled={isLoading}>
                                        {isLoading ? (
                                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                        ) : null}
                                        Sign in
                                    </Button>
                                </form>
                            </Form>

                            <div className="text-center text-sm">
                                <Link to="/auth/forgot-password" className="text-muted-foreground hover:text-primary underline underline-offset-4">
                                    Forgot your password?
                                </Link>
                            </div>

                        </>
                    )}

                    {mode === 'signup' && (
                        <>
                            <div className="relative">
                                <div className="absolute inset-0 flex items-center">
                                    <span className="w-full border-t" />
                                </div>
                                <div className="relative flex justify-center text-xs uppercase">
                                    <span className="bg-background px-2 text-muted-foreground">
                                        Or continue with email
                                    </span>
                                </div>
                            </div>

                            {/* Email/Password Sign Up */}
                            <Form {...form}>
                                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                                    <FormField
                                        control={form.control}
                                        name="name"
                                        render={({ field }) => (
                                            <FormItem>
                                                <FormLabel>Full Name</FormLabel>
                                                <FormControl>
                                                    <div className="relative">
                                                        <User className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                                                        <Input
                                                            placeholder="John Doe"
                                                            className="pl-9"
                                                            disabled={isLoading}
                                                            {...field}
                                                        />
                                                    </div>
                                                </FormControl>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />

                                    <FormField
                                        control={form.control}
                                        name="email"
                                        render={({ field }) => (
                                            <FormItem>
                                                <FormLabel>Email</FormLabel>
                                                <FormControl>
                                                    <div className="relative">
                                                        <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                                                        <Input
                                                            placeholder="you@example.com"
                                                            type="email"
                                                            className="pl-9"
                                                            disabled={isLoading}
                                                            {...field}
                                                        />
                                                    </div>
                                                </FormControl>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />

                                    <FormField
                                        control={form.control}
                                        name="password"
                                        render={({ field }) => (
                                            <FormItem>
                                                <FormLabel>Password</FormLabel>
                                                <FormControl>
                                                    <div className="relative">
                                                        <Input
                                                            placeholder="••••••••"
                                                            type={showPassword ? 'text' : 'password'}
                                                            autoComplete="new-password"
                                                            className="pr-10"
                                                            disabled={isLoading}
                                                            {...field}
                                                        />
                                                        <Button
                                                            type="button"
                                                            variant="ghost"
                                                            size="sm"
                                                            className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
                                                            onClick={() => setShowPassword(!showPassword)}
                                                            aria-label={showPassword ? 'Hide password' : 'Show password'}
                                                        >
                                                            {showPassword ? (
                                                                <EyeOff className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
                                                            ) : (
                                                                <Eye className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
                                                            )}
                                                        </Button>
                                                    </div>
                                                </FormControl>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />

                                    <FormField
                                        control={form.control}
                                        name="confirmPassword"
                                        render={({ field }) => (
                                            <FormItem>
                                                <FormLabel>Confirm Password</FormLabel>
                                                <FormControl>
                                                    <div className="relative">
                                                        <Input
                                                            placeholder="••••••••"
                                                            type={showConfirmPassword ? 'text' : 'password'}
                                                            autoComplete="new-password"
                                                            className="pr-10"
                                                            disabled={isLoading}
                                                            {...field}
                                                        />
                                                        <Button
                                                            type="button"
                                                            variant="ghost"
                                                            size="sm"
                                                            className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
                                                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                                            aria-label={showConfirmPassword ? 'Hide confirm password' : 'Show confirm password'}
                                                        >
                                                            {showConfirmPassword ? (
                                                                <EyeOff className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
                                                            ) : (
                                                                <Eye className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
                                                            )}
                                                        </Button>
                                                    </div>
                                                </FormControl>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />

                                    <Button type="submit" className="w-full" disabled={isLoading}>
                                        {isLoading ? (
                                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                        ) : null}
                                        Create account
                                    </Button>
                                </form>
                            </Form>
                        </>
                    )}

                </CardContent>
            </Card>

            {showFooter && mode === 'signin' && (
                <div className="text-center text-sm">
                    <p className="text-muted-foreground">
                        Don't have an account?{' '}
                        <Link to="/auth/signup" className="font-medium text-primary hover:underline">
                            Sign up
                        </Link>
                    </p>
                    <p className="mt-4 text-muted-foreground">
                        New here?{' '}
                        <Link to="/auth/signup" className="font-medium text-primary hover:underline">
                            Create an account
                        </Link>
                    </p>
                </div>
            )}

            {showFooter && mode === 'signup' && (
                <div className="text-center text-sm text-muted-foreground">
                    Already have an account?{' '}
                    <Link
                        to="/signin"
                        className="underline underline-offset-4 hover:text-primary"
                    >
                        Sign in
                    </Link>
                </div>
            )}
        </div>
    );
}
